import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import ParticleSphereAnimation from '@/components/ui/orbiting-circles-02-utils/particalsphear'

export interface OrbitNode {
  key: string
  /** Start angle in degrees; 0 is the top of the ring. */
  angle: number
  node: React.ReactNode
  /** Shown in a tooltip while the node is hovered or focused. */
  tooltip?: React.ReactNode
}

export interface Orbit {
  /** Ring diameter, as Tailwind width + height classes. */
  size: string
  /** Seconds per revolution. */
  duration: number
  nodes: OrbitNode[]
  /**
   * Duplicate every node at +180deg. The dome only shows the top half of
   * each ring, so mirroring keeps every node in view at all times.
   */
  mirror?: boolean
}

interface OrbitingCirclesGlobeProps {
  orbits: Orbit[]
  /** Content set still inside the globe, between its far and near particles. */
  center?: React.ReactNode
  /** Height of the visible dome, as Tailwind classes. */
  className?: string
  /** Globe diameter, as Tailwind width classes. */
  globeSize?: string
}

interface TooltipState {
  key: string
  content: React.ReactNode
  x: number
  y: number
  placement: 'above' | 'below'
  instant: boolean
}

const OPEN_DELAY = 120
/** Moving between nodes within this window opens the next tooltip instantly. */
const INSTANT_WINDOW = 400

/**
 * Half-dome orbit (adapted from 21st.dev "orbiting-circles-02"): concentric
 * rings centred on the bottom edge, nodes travelling round them with
 * alternating directions, each node counter-rotated so it stays upright, and
 * a particle globe rising from the centre.
 *
 * Every node sits at its start angle without animation, so reduced motion
 * shows a still, complete layout. Motion pauses off screen and while any
 * node is hovered.
 */
export default function OrbitingCirclesGlobe({
  orbits,
  center,
  className,
  globeSize = 'w-72 md:w-[34rem]',
}: OrbitingCirclesGlobeProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(true)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const [tooltipShown, setTooltipShown] = useState(false)
  const openTimer = useRef<number | undefined>(undefined)
  const lastClosed = useRef(0)

  const openTooltip = useCallback((key: string, content: React.ReactNode, el: HTMLElement) => {
    window.clearTimeout(openTimer.current)
    const instant = Date.now() - lastClosed.current < INSTANT_WINDOW
    const show = () => {
      const r = el.getBoundingClientRect()
      // Above the node when there is room, otherwise below its label.
      const placement = r.top > 150 ? 'above' : 'below'
      setTooltip({
        key,
        content,
        x: r.left + r.width / 2,
        y: placement === 'above' ? r.top - 12 : r.bottom + 52,
        placement,
        instant,
      })
      setTooltipShown(instant)
      if (!instant) requestAnimationFrame(() => requestAnimationFrame(() => setTooltipShown(true)))
    }
    if (instant) show()
    else openTimer.current = window.setTimeout(show, OPEN_DELAY)
  }, [])

  const closeTooltip = useCallback(() => {
    window.clearTimeout(openTimer.current)
    setTooltip((current) => {
      if (current) lastClosed.current = Date.now()
      return null
    })
    setTooltipShown(false)
  }, [])

  // A fixed tooltip would detach from its node on scroll: close it.
  useEffect(() => {
    if (!tooltip) return
    window.addEventListener('scroll', closeTooltip, { passive: true })
    return () => window.removeEventListener('scroll', closeTooltip)
  }, [tooltip, closeTooltip])

  useEffect(() => () => window.clearTimeout(openTimer.current), [])

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => setInView(entry?.isIntersecting ?? true), {
      threshold: 0.05,
    })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      data-paused={!inView}
      className={cn('oc-dome relative w-full h-96 md:h-[37rem] overflow-hidden flex justify-center', className)}
    >
      <style>{`
        @keyframes oc-orbit-cw {
          from { transform: rotate(var(--start-angle)) }
          to   { transform: rotate(calc(var(--start-angle) + 360deg)) }
        }
        @keyframes oc-orbit-ccw {
          from { transform: rotate(var(--start-angle)) }
          to   { transform: rotate(calc(var(--start-angle) - 360deg)) }
        }
        @keyframes oc-counter-cw {
          from { transform: rotate(var(--counter-offset, 0deg)) }
          to   { transform: rotate(calc(var(--counter-offset, 0deg) - 360deg)) }
        }
        @keyframes oc-counter-ccw {
          from { transform: rotate(var(--counter-offset, 0deg)) }
          to   { transform: rotate(calc(var(--counter-offset, 0deg) + 360deg)) }
        }
        /* !important: each node's inline animation shorthand resets the
           play state to running, which would otherwise win. */
        .oc-dome[data-paused="true"] .oc-anim,
        .oc-dome:has(.oc-node:hover) .oc-anim {
          animation-play-state: paused !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .oc-dome .oc-anim { animation: none !important; }
        }
      `}</style>

      {/* Centre particle globe. With centre content, the far hemisphere is
          drawn behind it and the near one in front, so it sits inside. */}
      <div
        className={cn(
          'absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 aspect-square pointer-events-none z-10',
          globeSize
        )}
      >
        {center ? (
          <>
            <div className="absolute inset-0">
              <ParticleSphereAnimation layer="back" />
            </div>
            <div className="absolute inset-x-0 bottom-1/2 mb-[9%] flex justify-center">{center}</div>
            <div className="absolute inset-0">
              <ParticleSphereAnimation layer="front" />
            </div>
          </>
        ) : (
          <ParticleSphereAnimation />
        )}
      </div>

      {/* Orbiting rings */}
      {orbits.map((orbit, index) => {
        const isCW = index % 2 === 0
        const orbitAnim = isCW ? 'oc-orbit-cw' : 'oc-orbit-ccw'
        const counterAnim = isCW ? 'oc-counter-cw' : 'oc-counter-ccw'
        const all = orbit.mirror
          ? [
              ...orbit.nodes,
              ...orbit.nodes.map((n) => ({ ...n, angle: n.angle + 180, key: `${n.key}-mirror`, mirror: true })),
            ]
          : orbit.nodes

        return (
          <div
            key={index}
            className={cn(
              // Rings are full-size boxes stacked over each other: let the
              // pointer through so only the nodes catch hover.
              'pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-full border border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]',
              orbit.size
            )}
          >
            {all.map((n) => (
              <div
                key={n.key}
                // Mirrors repeat content for the visual loop only.
                aria-hidden={'mirror' in n ? true : undefined}
                className="oc-anim absolute top-0 left-1/2 h-1/2 w-0 origin-bottom flex flex-col items-center justify-start"
                style={
                  {
                    '--start-angle': `${n.angle}deg`,
                    transform: `rotate(${n.angle}deg)`,
                    animation: `${orbitAnim} ${orbit.duration}s linear infinite`,
                  } as React.CSSProperties
                }
              >
                <div
                  tabIndex={n.tooltip && !('mirror' in n) ? 0 : undefined}
                  onMouseEnter={n.tooltip ? (e) => openTooltip(n.key, n.tooltip, e.currentTarget) : undefined}
                  onMouseLeave={n.tooltip ? closeTooltip : undefined}
                  onFocus={n.tooltip ? (e) => openTooltip(n.key, n.tooltip, e.currentTarget) : undefined}
                  onBlur={n.tooltip ? closeTooltip : undefined}
                  className="oc-anim oc-node pointer-events-auto relative -translate-y-1/2 z-20 shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-landing-accent)] focus-visible:ring-offset-4"
                  style={
                    {
                      '--counter-offset': `${-n.angle}deg`,
                      transform: `rotate(${-n.angle}deg)`,
                      animation: `${counterAnim} ${orbit.duration}s linear infinite`,
                    } as React.CSSProperties
                  }
                >
                  {n.node}
                </div>
              </div>
            ))}
          </div>
        )
      })}

      {tooltip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[60] w-max max-w-[17rem] bg-[var(--color-landing-gray-900)] px-4 py-3 text-left text-white shadow-[0_12px_32px_rgba(0,0,0,0.22)] dark:bg-white dark:text-[var(--color-landing-gray-900)]"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              translate: tooltip.placement === 'above' ? '-50% -100%' : '-50% 0',
              transformOrigin: tooltip.placement === 'above' ? 'bottom center' : 'top center',
              opacity: tooltipShown ? 1 : 0,
              transform: tooltipShown ? 'scale(1)' : 'scale(0.97)',
              transition: tooltip.instant
                ? 'none'
                : 'opacity 150ms cubic-bezier(0.23, 1, 0.32, 1), transform 150ms cubic-bezier(0.23, 1, 0.32, 1)',
            }}
          >
            {tooltip.content}
          </div>,
          document.body
        )}
    </div>
  )
}
